/**
 * ProfileMenu, the topbar avatar dropdown used by non-admin shells.
 *
 * Trigger: the same Avatar JSX used in AppShell's old utility cluster.
 * Content: a small dropdown with the user's name + email header, a
 * Settings link, and a destructive Sign Out item.
 *
 * Sign-out follows the same hard-nav pattern as AppShell's sidebar
 * footer button: clear React Query cache, then window.location.href =
 * '/auth' so the whole app remounts in a logged-out state.
 *
 * Both items respect the navigation guard (useGuardedAction) so the
 * wizard's unsaved-progress prompt still fires when applicable.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Settings as SettingsIcon } from 'lucide-react';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@insolech/ui';
import { useAuth } from '@/features/auth/AuthContext';
import { useGuardedAction } from './NavigationGuardContext';

export function ProfileMenu() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, signOut, fullName, avatarUrl } = useAuth();

  const displayName = fullName ?? user?.email ?? 'You';
  const email = user?.email ?? '';

  // Re-entrancy guard, same pattern as AppShell. Every click of the
  // Sign-out item kicks off an async chain (Supabase signOut, cache
  // clear, hard nav). Without this, a double-click could spawn two
  // signOut calls.
  const signingOutRef = React.useRef(false);
  const performSignOut = React.useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    try {
      await signOut();
    } catch (err) {
      console.warn('[ProfileMenu] signOut failed; forcing client-side logout', err);
    }
    queryClient.clear();
    // Hard nav so the entire app remounts in a logged-out state.
    window.location.href = '/auth';
  }, [signOut, queryClient]);

  const onSignOutProceed = React.useCallback(() => {
    void performSignOut();
  }, [performSignOut]);
  const handleSignOut = useGuardedAction(onSignOutProceed);

  const goToSettings = React.useCallback(() => {
    navigate('/settings');
  }, [navigate]);
  const handleSettings = useGuardedAction(goToSettings);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('nav.profile', { defaultValue: 'Open profile menu' })}
          className="inline-flex rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 max-sm:p-1.5 max-sm:-m-1.5"
          data-testid="profile-menu-trigger"
        >
          <span className="sr-only">
            {t('nav.profile', { defaultValue: 'Open profile menu' })}
          </span>
          <Avatar
            name={displayName}
            {...(avatarUrl ? { src: avatarUrl } : {})}
            size="sm"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-[240px]">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-text-primary truncate">
            {displayName}
          </span>
          {email ? (
            <span className="text-xs text-text-muted truncate">{email}</span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(event) => {
            // Prevent Radix's default close-then-blur dance from
            // racing with the guard prompt; we close + navigate
            // manually via the guarded action.
            event.preventDefault();
            void handleSettings();
          }}
          data-testid="profile-menu-settings"
        >
          <SettingsIcon className="h-4 w-4" aria-hidden="true" />
          <span>{t('nav.settings', { defaultValue: 'Settings' })}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={(event) => {
            event.preventDefault();
            void handleSignOut();
          }}
          data-testid="profile-menu-sign-out"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <span>{t('action_sign_out')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
